import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { get, post, api, type LibraryResponse, type LibraryPortrait } from "../lib/api";
import { capture } from "../lib/analytics";
import { Header, Spinner, Muted, ErrorText, GildedFrame, PrimaryButton, QuietButton, Field, Empty } from "../components/ui";
import { PrintMockup } from "../components/PrintMockup";

// Prints reuse the ENTIRE server pipeline — pricing, Stripe Tax, promo codes,
// shipping protection, the 4x upscale, the Gelato order, webhooks, the Radar hold
// and refund-exactly-once. The only thing web replaces is the native PaymentSheet:
// Stripe.js drives the same clientSecret. No server change, no second pipeline.

// Mirrors catalogView() in worker/src/print.ts exactly. The first pass invented
// sku/title/retailUSD/mockup — none of which the API returns — so cards rendered
// with no title, no price, no image, and the cart would have posted an undefined
// sku to checkout.
interface CatalogVariant {
  id: string;
  label: string;
  priceCents: number;
  size?: string;
  aspect?: string;
  colorId?: string;
  colorLabel?: string;
  colorHex?: string;
}
interface CatalogProduct {
  id: string;
  label: string;
  category: string;
  framed: boolean;
  aspect: string;
  priceCents: number;
  /** true → variants differ in price, so show "From $X" */
  priceFrom?: boolean;
  variants?: CatalogVariant[];
}
interface CatalogResponse { products: CatalogProduct[]; currency: string; freeShipping: boolean; publishableKey: string }

interface CartLine { sku: string; variantId?: string; portraitUrl: string; qty: number; label: string; }

export function Prints() {
  const [params] = useSearchParams();
  const [catalog, setCatalog] = useState<CatalogProduct[] | null>(null);
  const [lib, setLib] = useState<LibraryResponse | null>(null);
  const [portrait, setPortrait] = useState<string | null>(params.get("portrait"));
  const [cart, setCart] = useState<CartLine[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [c, l] = await Promise.all([get<CatalogResponse>("/v1/print/catalog"), api.library()]);
        setCatalog(c.products);
        setLib(l);
        capture("webPrintsViewed");
      } catch {
        setErr("Couldn't load the print shop. Please refresh.");
      }
    })();
  }, []);

  const portraits = useMemo(() => (lib?.eras ?? []).flatMap((e) => e.portraits), [lib]);

  if (err) return <ErrorText>{err}</ErrorText>;
  if (!catalog || !lib) return <Spinner />;

  if (portraits.length === 0) {
    return (
      <>
        <Header kicker="Prints" title="Order a print" />
        <Empty
          title="Nothing to print yet"
          body="Make a gallery first, then come back and put your favourite on a wall, a mug, or a tee."
          action={<Link to="/create"><QuietButton>Create a gallery</QuietButton></Link>}
        />
      </>
    );
  }

  if (checkingOut) {
    return <Checkout cart={cart} onBack={() => setCheckingOut(false)} />;
  }

  return (
    <>
      <Header kicker="Prints" title="Order a print" />

      <section className="mb-7">
        <h2 className="mb-2 font-serif text-lg text-ivory">1. Pick a portrait</h2>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {portraits.map((p: LibraryPortrait) => (
            <button
              key={p.url}
              onClick={() => setPortrait(p.url)}
              className={`shrink-0 overflow-hidden rounded-lg border-2 transition ${
                portrait === p.url ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={p.url} alt={p.label} className="h-24 w-24 object-cover" />
            </button>
          ))}
        </div>
      </section>

      <section className="mb-7">
        <h2 className="mb-2 font-serif text-lg text-ivory">2. Choose a product</h2>
        {!portrait && <Muted className="mb-3">Pick a portrait first.</Muted>}
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {catalog.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              portraitUrl={portrait}
              disabled={!portrait}
              onAdd={(variantId, label) => {
                if (!portrait) return;
                setCart((c) => [...c, { sku: p.id, variantId, portraitUrl: portrait, qty: 1, label }]);
                capture("webPrintAddedToCart", { sku: p.id, variantId });
              }}
            />
          ))}
        </div>
      </section>

      {cart.length > 0 && (
        <section className="sticky bottom-4">
          <GildedFrame>
            <div className="p-4">
              <h2 className="mb-2 font-serif text-lg text-ivory">Your cart</h2>
              {cart.map((l, i) => (
                <div key={i} className="flex items-center justify-between border-b border-gold/10 py-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <img src={l.portraitUrl} alt="" className="h-9 w-9 rounded object-cover" />
                    <span className="text-[13px] text-parchment">{l.label}</span>
                  </div>
                  <button
                    onClick={() => setCart((c) => c.filter((_, j) => j !== i))}
                    className="text-[12px] text-stoneDim hover:text-reject"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="mt-3">
                <PrimaryButton onClick={() => setCheckingOut(true)}>
                  Checkout ({cart.length} {cart.length === 1 ? "item" : "items"})
                </PrimaryButton>
                <Muted className="mt-2 text-center text-[11px]">Shipping and tax calculated next.</Muted>
              </div>
            </div>
          </GildedFrame>
        </section>
      )}
    </>
  );
}

function ProductCard({
  product,
  portraitUrl,
  disabled,
  onAdd,
}: {
  product: CatalogProduct;
  portraitUrl: string | null;
  disabled: boolean;
  onAdd: (variantId: string | undefined, label: string) => void;
}) {
  const [variantId, setVariantId] = useState(product.variants?.[0]?.id);
  const variant = product.variants?.find((v) => v.id === variantId);
  const cents = variant?.priceCents ?? product.priceCents;
  const price = `${product.priceFrom && !variant ? "From " : ""}$${(cents / 100).toFixed(2)}`;
  return (
    <GildedFrame className="h-full">
      <div className="p-3">
        {/* The real preview: the chosen portrait composited into the product photo. */}
        <PrintMockup
          category={product.category}
          portraitUrl={portraitUrl}
          colorId={variant?.colorId}
          className="mb-2"
        />
        <div className="font-serif text-[15px] text-ivory">{product.label}</div>
        {product.variants && product.variants.length > 0 && (
          <select
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            className="mt-2 w-full rounded border border-gold/25 bg-screenInk px-2 py-1.5 text-[12px] text-parchment"
          >
            {product.variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[14px] font-semibold text-goldBright">{price}</span>
          <QuietButton
            disabled={disabled}
            onClick={() => onAdd(variantId, `${product.label}${variant ? ` · ${variant.label}` : ""}`)}
          >
            Add
          </QuietButton>
        </div>
      </div>
    </GildedFrame>
  );
}

// ── Checkout ────────────────────────────────────────────────────────

interface Breakdown { itemsCents: number; shippingCents: number; taxCents: number; totalCents: number; currency: string }

function Checkout({ cart, onBack }: { cart: CartLine[]; onBack: () => void }) {
  const [addr, setAddr] = useState({
    firstName: "", lastName: "", addressLine1: "", addressLine2: "",
    city: "", state: "", postCode: "", country: "US", email: "",
  });
  // Web is the natural home for gifting (faith/family, Christmas, baptisms), and
  // the order model already carries its own recipient + email, so this is a
  // labelling change rather than a new pipeline.
  const [isGift, setIsGift] = useState(false);
  const [protect, setProtect] = useState(true);
  const [intent, setIntent] = useState<{ clientSecret: string; publishableKey: string; breakdown: Breakdown } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canQuote = addr.firstName && addr.lastName && addr.addressLine1 && addr.city && addr.postCode && addr.email;

  async function startCheckout() {
    setBusy(true);
    setErr(null);
    try {
      const res = await post<{ clientSecret: string; publishableKey: string; breakdown: Breakdown }>(
        "/v1/print/cart/checkout",
        {
          items: cart.map((l) => ({ sku: l.sku, variantId: l.variantId, portraitUrl: l.portraitUrl, qty: l.qty })),
          address: addr,
          shippingProtection: protect,
        },
      );
      setIntent(res);
      capture("webPrintCheckoutStarted", { items: cart.length, gift: isGift, total: res.breakdown.totalCents });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't start checkout.");
    }
    setBusy(false);
  }

  if (intent) {
    return (
      <PayStep
        clientSecret={intent.clientSecret}
        publishableKey={intent.publishableKey}
        breakdown={intent.breakdown}
        onBack={() => setIntent(null)}
      />
    );
  }

  const f = (k: keyof typeof addr, ph: string, extra: Record<string, unknown> = {}) => (
    <Field placeholder={ph} value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} {...extra} />
  );

  return (
    <div className="mx-auto max-w-md">
      <Header kicker="Checkout" title={isGift ? "Ship to your giftee" : "Ship to"} />
      <GildedFrame>
        <div className="space-y-2.5 p-4">
          <label className="mb-1 flex cursor-pointer items-center gap-2 text-[13px] text-parchment">
            <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)} className="accent-gold" />
            This is a gift, ship it to someone else
          </label>
          {isGift && (
            <Muted className="!mt-0 pb-1 text-[11px]">
              We'll send tracking to your email, and nothing with a price on it goes in the box.
            </Muted>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            {f("firstName", isGift ? "Their first name" : "First name")}
            {f("lastName", isGift ? "Their last name" : "Last name")}
          </div>
          {f("addressLine1", "Address")}
          {f("addressLine2", "Apt, suite (optional)")}
          <div className="grid grid-cols-3 gap-2.5">
            {f("city", "City")}
            {f("state", "State")}
            {f("postCode", "ZIP")}
          </div>
          {f("country", "Country", { maxLength: 2 })}
          {f("email", isGift ? "Your email (for tracking)" : "Email", { type: "email" })}

          <label className="flex cursor-pointer items-center gap-2 pt-1 text-[13px] text-parchment">
            <input type="checkbox" checked={protect} onChange={(e) => setProtect(e.target.checked)} className="accent-gold" />
            Shipping protection
          </label>

          <ErrorText>{err}</ErrorText>
          <PrimaryButton onClick={startCheckout} busy={busy} busyLabel="Pricing…" disabled={!canQuote}>
            Continue to payment
          </PrimaryButton>
          <div className="flex justify-center pt-1">
            <QuietButton onClick={onBack}>Back to cart</QuietButton>
          </div>
        </div>
      </GildedFrame>
    </div>
  );
}

function PayStep({
  clientSecret,
  publishableKey,
  breakdown,
  onBack,
}: {
  clientSecret: string;
  publishableKey: string;
  breakdown: Breakdown;
  onBack: () => void;
}) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;
  return (
    <div className="mx-auto max-w-md">
      <Header kicker="Checkout" title="Payment" />
      <GildedFrame>
        <div className="p-4">
          <div className="mb-4 space-y-1 text-[13px]">
            <Row label="Items" value={money(breakdown.itemsCents)} />
            <Row label="Shipping" value={money(breakdown.shippingCents)} />
            <Row label="Tax" value={money(breakdown.taxCents)} />
            <div className="mt-2 border-t border-gold/15 pt-2">
              <Row label="Total" value={money(breakdown.totalCents)} bold />
            </div>
          </div>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: "night",
                variables: { colorPrimary: "#C9A24B", colorBackground: "#0C0805", colorText: "#F2E8D5" },
              },
            }}
          >
            <PayForm onBack={onBack} />
          </Elements>
        </div>
      </GildedFrame>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? "text-ivory" : "text-warmStone"}>{label}</span>
      <span className={bold ? "font-semibold text-goldBright" : "text-parchment"}>{value}</span>
    </div>
  );
}

function PayForm({ onBack }: { onBack: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setBusy(true);
        setErr(null);
        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: `${window.location.origin}/orders` },
        });
        // We only get here if confirmation failed; success redirects away. The
        // Stripe webhook is the authority on fulfilment either way, so a lost
        // redirect can't lose the order.
        if (error) setErr(error.message || "Payment didn't go through.");
        setBusy(false);
      }}
    >
      <PaymentElement />
      <ErrorText>{err}</ErrorText>
      <div className="mt-4">
        <PrimaryButton type="submit" busy={busy} busyLabel="Paying…" disabled={!stripe}>
          Pay now
        </PrimaryButton>
        <div className="mt-2 flex justify-center">
          <QuietButton type="button" onClick={onBack}>
            Back
          </QuietButton>
        </div>
      </div>
    </form>
  );
}
